/**
 * Sheet-aware display label for documents produced by the multi-sheet
 * workbook ingestion pipeline. When a document carries
 * `object_metadata.sheet_name` (populated by the per-sheet upload loop in
 * MSW-5), display surfaces compose `${name} — ${sheet_name}` so each row
 * is distinguishable in the queue, show page, downloads, and search.
 *
 * Documents without a sheet name (PDFs, CSVs, single-sheet workbooks)
 * fall through to the literal `name` — preserves the pre-MSW UX.
 *
 * MSW-8 / ADO #4477.
 */

/** Minimum shape needed to compose the label — accepts any document-like record. */
export interface DocumentLabelSource {
  name?: string;
  object_metadata?: {
    sheet_name?: string;
  };
}

const FALLBACK_NAME = "Unnamed Document";

/**
 * Composes the visible label. Em-dash separator (matches AC8.1 wording).
 * Missing `name` falls back to "Unnamed Document"; missing `sheet_name`
 * returns the bare name (no separator). Never throws.
 */
export const composeDocumentLabel = (record: DocumentLabelSource | null | undefined): string => {
  const name = record?.name?.trim() || FALLBACK_NAME;
  const sheet = record?.object_metadata?.sheet_name?.trim();
  return sheet ? `${name} — ${sheet}` : name;
};

/**
 * Screen-reader-friendly aria-label for the composed label. Replaces the
 * em-dash with the word "sheet" so assistive tech reads "report.xlsx,
 * sheet Financial Summary" rather than "report.xlsx em dash Financial
 * Summary." Documents without a sheet name return the bare name.
 *
 * Pair with `<span aria-hidden="true">` around the em-dash in the visual
 * label so the dash is announced once via aria-label rather than twice.
 *
 * MSW-8 / ADO #4477 AC8.5.
 */
export const composeDocumentLabelAria = (record: DocumentLabelSource | null | undefined): string => {
  const name = record?.name?.trim() || FALLBACK_NAME;
  const sheet = record?.object_metadata?.sheet_name?.trim();
  return sheet ? `${name}, sheet ${sheet}` : name;
};

/**
 * Sheet-name subtitle for PageHeader on the document show page. Returns
 * `Sheet: ${sheet_name}` when set, or undefined when absent. PageHeader's
 * 600px title cap eats long filenames before the sheet name fits — so the
 * show page splits the label across title (filename) and subtitle (sheet)
 * instead of using the inline composed form. Inline composed form is
 * still used by the queue and per-document tabs where horizontal space
 * is less constrained.
 *
 * MSW-8 / ADO #4477 AC8.1.
 */
export const composeDocumentSubtitle = (record: DocumentLabelSource | null | undefined): string | undefined => {
  const sheet = record?.object_metadata?.sheet_name?.trim();
  return sheet ? `Sheet: ${sheet}` : undefined;
};
